import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  OutlinedInput,
  SvgIcon,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// --- Styled Components ---

const Label = styled(Typography)({
  color: "#555",
  fontFamily: "Inter, sans-serif",
  fontSize: "0.875rem",
  fontWeight: 400,
  lineHeight: "140%",
  marginBottom: "8px",
});

// We use transient prop prefix "$" for minWidth to avoid passing it to the DOM element
const StyledSelect = styled(Select)(({ $minWidth }) => ({
  width: "100%",
  minWidth: $minWidth !== undefined && $minWidth !== "" ? $minWidth : "auto",

  "& .MuiOutlinedInput-notchedOutline": {
    border: "1px solid #BBB",
    borderRadius: "8px",
    transition: "all 0.2s ease-in-out",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#7A7A7A",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "1px solid #003A5D",
    borderWidth: "1px", // Keep it 1px to prevent jumping
  },
  "& .MuiSelect-select": {
    padding: "11.5px 16px",
    backgroundColor: "#FFF",
    borderRadius: "8px",
    fontFamily: "Inter, sans-serif",
    fontSize: "1rem",
    lineHeight: "140%",
    color: "#333",
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .MuiSelect-icon": {
    right: "12px",
    top: "calc(50% - 10px)",
  },
  // Style for disabled state
  "&.Mui-disabled": {
    backgroundColor: "#F5F5F5",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#DDD",
    },
  },
}));

const DropdownIcon = (props) => (
  <SvgIcon
    {...props}
    sx={{ width: 20, height: 20 }}
    viewBox="0 0 20 20"
    style={{ fill: "none" }}
  >
    <path
      d="M10.4865 14L4 7.51351L5.51351 6L10.4865 10.9459L15.4595 6L16.973 7.51351L10.4865 14Z"
      fill="#003A5D"
    />
  </SvgIcon>
);

// --- Main Export ---

const CustomizedDropdown = ({
  label,
  placeholder,
  value,
  onChange,
  options = [],
  minWidth = "auto",
  disabled = false,
}) => {
  return (
    <Box sx={{ width: "100%" }}>
      {label && <Label>{label}</Label>}

      <FormControl
        sx={{ width: "100%", minWidth: minWidth !== "auto" ? minWidth : "0" }}
        disabled={disabled}
      >
        <StyledSelect
          displayEmpty
          value={value}
          onChange={onChange}
          input={<OutlinedInput />}
          IconComponent={DropdownIcon}
          $minWidth={minWidth}
          renderValue={(selected) => {
            if (!selected) {
              return <span style={{ color: "#7A7A7A" }}>{placeholder}</span>;
            }
            const selectedOption = options.find(
              (opt) => opt.value === selected,
            );
            return selectedOption ? selectedOption.label : selected;
          }}
          MenuProps={{
            // Removed e.stopPropagation() to allow clicks outside to close the menu
            autoFocus: false,
            disableScrollLock: true,
            PaperProps: {
              sx: {
                borderRadius: "8px",
                marginTop: "4px",
                minWidth: minWidth !== "auto" ? minWidth : "auto",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                // No zIndex needed usually as Portal handles this
              },
            },
          }}
        >
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </StyledSelect>
      </FormControl>
    </Box>
  );
};

export default CustomizedDropdown;
